import { Link } from 'react-router-dom';
import { FolderKanban, FolderPlus, Sparkles, X } from 'lucide-react';
import type { ThreadSummary } from '@mailpilot/shared';
import { useAddToProject, useDismissProjectSuggestion, useProjects } from '../../hooks/useProjects';
import { useCurrentWorkspace } from '../../hooks/useWorkspace';
import { Button } from '../ui';
import { Menu } from '../ui/Overlay';

/** Chips for the projects a conversation is in (organizations). */
export function ThreadProjectChips({ thread }: { thread: ThreadSummary }) {
  if (!thread.projects?.length) return null;
  return (
    <>
      {thread.projects.map((p) => (
        <Link key={p.id} to={`/projects/${p.id}/${thread.id}`} className="mp-pill mp-pill-mint hover:underline">
          <FolderKanban className="h-3 w-3" aria-hidden /> {p.name}
        </Link>
      ))}
    </>
  );
}

/** Header button: add this conversation to one of your projects. */
export function AddToProjectButton({ thread }: { thread: ThreadSummary }) {
  const { isOrg } = useCurrentWorkspace();
  const projects = useProjects(isOrg).data?.projects ?? [];
  const add = useAddToProject();
  const inIds = new Set((thread.projects ?? []).map((p) => p.id));
  const options = projects.filter((p) => !inIds.has(p.id) && (p.canManage || p.me));
  if (!isOrg || !options.length) return null;
  return (
    <Menu
      label="Add to project"
      align="right"
      width={260}
      header={<p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">Add to project</p>}
      trigger={(p) => (
        <button type="button" {...p} aria-label="Add to project" data-tip="Add to project" className="tip flex h-9 w-9 items-center justify-center rounded-xl text-muted transition-colors hover:bg-raised hover:text-ink">
          <FolderPlus className="h-[18px] w-[18px]" />
        </button>
      )}
      items={options.map((p) => ({ key: p.id, label: p.name, icon: <FolderKanban className="h-4 w-4" />, onSelect: () => add.mutate({ projectId: p.id, threadId: thread.id }) }))}
    />
  );
}

/** "Looks like this belongs to …" — the AI/heuristic suggestion for a conversation in no project. */
export function ProjectSuggestion({ thread }: { thread: ThreadSummary }) {
  const { isOrg } = useCurrentWorkspace();
  const projects = useProjects(isOrg).data?.projects ?? [];
  const add = useAddToProject();
  const dismiss = useDismissProjectSuggestion();
  const s = thread.suggestedProject;
  if (!isOrg || !s || thread.projects?.length) return null;
  const canManage = projects.find((p) => p.id === s.id)?.canManage;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-primary/25 bg-primary-soft/60 px-4 py-3">
      <Sparkles className="h-4 w-4 shrink-0 text-primary-ink" aria-hidden />
      <p className="min-w-0 flex-1 text-[0.8125rem] text-ink">
        Looks like this belongs to <strong className="font-semibold">{s.name}</strong>
        {s.reason && <span className="text-muted"> — {s.reason}</span>}
      </p>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="primary" loading={add.isPending} onClick={() => add.mutate({ projectId: s.id, threadId: thread.id })}>Add</Button>
        {canManage && <Button size="sm" onClick={() => add.mutate({ projectId: s.id, threadId: thread.id, alwaysForSender: true })}>Add &amp; always for this sender</Button>}
        <button type="button" aria-label="Not this project" onClick={() => dismiss.mutate(thread.id)} className="rounded-lg p-1.5 text-muted hover:bg-raised hover:text-ink">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
